/**
 * Pack Recommendation Engine
 * Analyzes team state and suggests optimal pack choices
 * Based on Phase 3 feature specification requirements
 *
 * COMPOSITION CONTRACT
 * All roster counts/limits come from `normalizeComposition()` — the single
 * reader for the backend composition payload. See draftComposition.js for the
 * verified shapes.
 *
 * Roster limits are catalog-driven and vary per league template (a season
 * catalog is 2 QB / 4 RB / 3 WR; a daily catalog is 1/2/2). This engine
 * therefore NEVER substitutes a hardcoded roster size: an unknown limit is
 * `null` (already logged loudly by the normalizer) and the corresponding
 * heuristic is skipped rather than scored against a wrong number.
 */

import {
    normalizeComposition,
    getPositionCount,
    getPositionLimit,
    getPositionNeed
} from './draftComposition';

/**
 * Read the number of packs the team has already completed off a draft_state
 * payload.
 *
 * The backend field is `total_packs_completed` — TeamDraftStateSerializer.Meta.fields,
 * backend/game/api/serializers/pack_serializers.py:204-209. There is no
 * `packs_completed` key on draft_state.
 *
 * Returns null (after a loud console.error) when the field is absent. Callers
 * SKIP the corresponding heuristic rather than substituting 0 — assuming 0 would
 * silently hand out the "early draft" bonus to a late-draft team. A genuine 0
 * from the backend still means "no packs completed yet".
 */
function readPacksCompleted(teamState, context) {
    const packsCompleted = teamState?.total_packs_completed;
    if (typeof packsCompleted !== 'number') {
        console.error(
            `[PackRecommendationEngine:${context}] CONTRACT VIOLATION: ` +
            'draft_state.total_packs_completed is missing or not a number. ' +
            'The backend guarantees this field on draft_state.',
            {
                total_packs_completed: packsCompleted,
                total_packs_completed_typeof: typeof packsCompleted,
                teamStateKeys: teamState ? Object.keys(teamState) : null
            }
        );
        return null;
    }
    return packsCompleted;
}

export class PackRecommendationEngine {
    static getRecommendations(availablePacks, teamState, userPreferences = {}) {
        return availablePacks.map(pack => ({
            ...pack,
            recommendation_score: this.calculateRecommendationScore(pack, teamState),
            recommendation_reason: this.generateRecommendationReason(pack, teamState),
            urgency_level: this.calculateUrgencyLevel(pack, teamState)
        })).sort((a, b) => b.recommendation_score - a.recommendation_score);
    }
    
    static calculateRecommendationScore(pack, teamState) {
        let score = 0;
        
        // Base score by rarity
        if (pack.rarity_tier === 'LEGENDARY') score += 30;
        else if (pack.rarity_tier === 'RARE') score += 20;
        else score += 10;
        
        // Position need bonuses
        const composition = normalizeComposition(teamState, 'PackRecommendationEngine.score');
        
        // Critical QB need
        const qbCurrent = getPositionCount(composition, 'qb');
        if (qbCurrent === 0) {
            if (pack.expected_qb_probability > 0.3) score += 50;
            else if (pack.expected_qb_probability > 0) score += 20;
        }
        
        // RB need — null need means the payload didn't supply count/limit;
        // skip rather than score against a guessed roster size.
        const rbNeed = getPositionNeed(composition, 'rb');
        if (rbNeed !== null && rbNeed > 0) {
            score += (pack.expected_rb_probability || 0) * 30 * rbNeed;
        }
        
        // WR need
        const wrNeed = getPositionNeed(composition, 'wr');
        if (wrNeed !== null && wrNeed > 0) {
            score += (pack.expected_wr_probability || 0) * 30 * wrNeed;
        }
        
        // Gear balance
        const gearNeed = getPositionNeed(composition, 'gear');
        if (gearNeed !== null && gearNeed > 0) {
            score += (pack.expected_item_probability || 0) * 15;
        }
        
        // Early draft bonus for high rarity. Unknown progress → skip the bonus
        // rather than assume an early draft.
        const packsCompleted = readPacksCompleted(teamState, 'score');
        if (packsCompleted !== null && packsCompleted < 5 && pack.rarity_tier === 'LEGENDARY') {
            score += 25;
        }
        
        // Factor in pack configuration bonuses
        if (pack.pack_config) {
            // Bonus for guaranteed cards
            if (pack.pack_config.guaranteed_qb) score += 40;
            if (pack.pack_config.guaranteed_rb) score += 25;
            if (pack.pack_config.guaranteed_wr) score += 25;
            if (pack.pack_config.guaranteed_gear) score += 15;
        }
        
        return Math.round(score);
    }
    
    /**
     * True only when we KNOW the team still has room at this position.
     * Unknown count or limit → false (never a hardcoded assumption).
     */
    static hasRoomFor(composition, position) {
        const need = getPositionNeed(composition, position);
        return need !== null && need > 0;
    }
    
    static generateRecommendationReason(pack, teamState) {
        const composition = normalizeComposition(teamState, 'PackRecommendationEngine.reason');
        const packsCompleted = readPacksCompleted(teamState, 'reason');
        
        // Get current counts (null = unknown; already logged by the normalizer)
        const qbCurrent = getPositionCount(composition, 'qb');
        const rbCurrent = getPositionCount(composition, 'rb');
        const wrCurrent = getPositionCount(composition, 'wr');
        const gearCurrent = getPositionCount(composition, 'gear');
        
        // Critical needs
        if (qbCurrent === 0) {
            if (pack.expected_qb_probability > 0.5 || pack.pack_config?.guaranteed_qb) {
                return "🎯 High QB chance - you need a quarterback!";
            } else if (pack.expected_qb_probability > 0) {
                return "⚡ Some QB chance - quarterback needed";
            }
        }
        
        // Position building
        if (this.hasRoomFor(composition, 'rb') && pack.expected_rb_probability > 0.4) {
            return "🏃 Great for building running back depth";
        }
        
        if (this.hasRoomFor(composition, 'wr') && pack.expected_wr_probability > 0.4) {
            return "🎯 Excellent for wide receiver options";
        }
        
        // Early draft strategy. Unknown progress → skip this reason rather than
        // claim an early draft we can't verify.
        if (packsCompleted !== null && packsCompleted < 3 && pack.rarity_tier === 'LEGENDARY') {
            return "⭐ Premium pack - grab elite players early";
        }
        
        // Pack configuration specific reasons
        if (pack.pack_config) {
            if (pack.pack_config.guaranteed_qb && qbCurrent === 0) {
                return "🔒 Guaranteed QB - exactly what you need!";
            }
            if (pack.pack_config.guaranteed_rb && this.hasRoomFor(composition, 'rb')) {
                return "🔒 Guaranteed RB - solid foundation";
            }
            if (pack.pack_config.guaranteed_wr && this.hasRoomFor(composition, 'wr')) {
                return "🔒 Guaranteed WR - reliable option";
            }
            if (pack.pack_config.theme === 'OFFENSE' &&
                qbCurrent !== null && rbCurrent !== null && wrCurrent !== null &&
                (qbCurrent + rbCurrent + wrCurrent) < 3) {
                return "🏈 Offense-focused pack - build your core";
            }
            if (pack.pack_config.theme === 'DEFENSE' && gearCurrent !== null && gearCurrent < 3) {
                return "🛡️ Defense pack - enhance your players";
            }
        }
        
        // Rarity-based recommendations
        if (pack.rarity_tier === 'LEGENDARY') {
            return "⭐ Best possible players and gear available";
        } else if (pack.rarity_tier === 'RARE') {
            return "💎 High-quality options for your team";
        }
        
        // Balanced choice
        return "⚖️ Solid choice for team building";
    }
    
    static calculateUrgencyLevel(pack, teamState) {
        const composition = normalizeComposition(teamState, 'PackRecommendationEngine.urgency');
        
        // Get current counts
        const qbCurrent = getPositionCount(composition, 'qb');
        const rbCurrent = getPositionCount(composition, 'rb');
        const wrCurrent = getPositionCount(composition, 'wr');
        
        // Critical if no QB
        if (qbCurrent === 0 && (pack.expected_qb_probability > 0.3 || pack.pack_config?.guaranteed_qb)) {
            return 'critical';
        }
        
        // High if filling key positions (an empty slot is urgent regardless of
        // roster size, so this only needs the count).
        if (rbCurrent === 0 && pack.expected_rb_probability > 0.4) {
            return 'high';
        }
        
        if (wrCurrent === 0 && pack.expected_wr_probability > 0.4) {
            return 'high';
        }
        
        // Medium for legendary packs
        if (pack.rarity_tier === 'LEGENDARY') {
            return 'medium';
        }
        
        // Medium for guaranteed packs that fill needs
        if (pack.pack_config) {
            if ((pack.pack_config.guaranteed_rb && this.hasRoomFor(composition, 'rb')) ||
                (pack.pack_config.guaranteed_wr && this.hasRoomFor(composition, 'wr'))) {
                return 'medium';
            }
        }
        
        return 'low';
    }
    
    static getUrgencyColor(urgencyLevel) {
        switch (urgencyLevel) {
            case 'critical': return '#e74c3c';
            case 'high': return '#f39c12';
            case 'medium': return '#3498db';
            case 'low': return '#95a5a6';
            default: return '#95a5a6';
        }
    }
    
    static getUrgencyLabel(urgencyLevel) {
        switch (urgencyLevel) {
            case 'critical': return 'MUST HAVE';
            case 'high': return 'RECOMMENDED';
            case 'medium': return 'GOOD CHOICE';
            case 'low': return 'OPTION';
            default: return 'OPTION';
        }
    }
    
    static filterPacksByNeed(availablePacks, teamState, needType) {
        const composition = normalizeComposition(teamState, 'PackRecommendationEngine.filter');
        
        // Position filters hide packs once a position is FULL. With an unknown
        // limit we cannot know that, so we keep showing packs (the permissive
        // side) rather than hiding options based on a guessed roster size.
        const isPositionFull = (position) => {
            const need = getPositionNeed(composition, position);
            return need !== null && need === 0;
        };
        
        switch (needType) {
            case 'qb':
                if (isPositionFull('qb')) return [];
                return availablePacks.filter(pack =>
                    pack.expected_qb_probability > 0 || pack.pack_config?.guaranteed_qb
                );
                
            case 'rb':
                if (isPositionFull('rb')) return [];
                return availablePacks.filter(pack =>
                    pack.expected_rb_probability > 0.2 || pack.pack_config?.guaranteed_rb
                );
                
            case 'wr':
                if (isPositionFull('wr')) return [];
                return availablePacks.filter(pack =>
                    pack.expected_wr_probability > 0.2 || pack.pack_config?.guaranteed_wr
                );
                
            case 'gear':
                if (isPositionFull('gear')) return [];
                return availablePacks.filter(pack =>
                    pack.expected_item_probability > 0.2 || pack.pack_config?.guaranteed_gear
                );
                
            case 'legendary':
                return availablePacks.filter(pack => pack.rarity_tier === 'LEGENDARY');
                
            default:
                return availablePacks;
        }
    }
    
    static getTeamNeedsSummary(teamState) {
        const composition = normalizeComposition(teamState, 'PackRecommendationEngine.needs');
        
        const needs = [];
        
        // Positions with an unknown count or limit are omitted from the summary
        // entirely — a need computed against a hardcoded roster size would be
        // wrong for every non-daily catalog. The normalizer has already logged.
        const addNeed = (position, label, priorityFor) => {
            const current = getPositionCount(composition, position);
            const limit = getPositionLimit(composition, position);
            const needed = getPositionNeed(composition, position);
            if (needed === null || needed <= 0) return;
            needs.push({
                position: label,
                priority: priorityFor(current),
                needed,
                current,
                max: limit
            });
        };
        
        addNeed('qb', 'QB', current => (current === 0 ? 'critical' : 'medium'));
        addNeed('rb', 'RB', current => (current === 0 ? 'high' : 'medium'));
        addNeed('wr', 'WR', current => (current === 0 ? 'high' : 'medium'));
        addNeed('gear', 'GEAR', current => (current < 2 ? 'medium' : 'low'));
        
        return needs.sort((a, b) => {
            const priorityOrder = { 'critical': 0, 'high': 1, 'medium': 2, 'low': 3 };
            return priorityOrder[a.priority] - priorityOrder[b.priority];
        });
    }
}
